"use client";

import { createContext } from "react";
import type { AgentState, ChatConversation } from "@huub-web/ui";
import type {
  AgentResponse,
  AppContext,
  WebSocketFeedbackMessage,
} from "@huub-web/backend";
import type { TemplateCollection } from "@huub-web/templating";

export type ChatMessageData = {
  feedback?: WebSocketFeedbackMessage["data"];
  templates?: TemplateCollection;
};

export type AppControllerContextState = AppContext & {
  /**
   * Whether the TTS is enabled.
   */
  ttsEnabled?: boolean;
  /**
   * The agent state.
   */
  state: AgentState;
  /**
   * The chat conversation.
   */
  conversation: ChatConversation<ChatMessageData>;
  /**
   * The agent responses.
   */
  agentResponses: Record<string, AgentResponse<TemplateCollection>>;
  /**
   * The IDs of the agent responses.
   */
  agentResponseIDs: string[];
  /**
   * The current agent response.
   */
  currentAgentResponse: AgentResponse<TemplateCollection> | null;
  /**
   * Whether the agent is speaking.
   */
  isSpeaking: boolean;
  /**
   * Whether the agent is streaming a message.
   */
  isMessageStreaming: boolean;
  /**
   * Whether the app is waiting for an agent response.
   */
  isWaitingForAgentResponse: boolean;
  /**
   * Whether the audio context is running.
   */
  isAudioContextRunning: boolean;
  /**
   * Whether the app is loading.
   */
  isLoading: boolean;
  /**
   * Adds an agent response to the chat.
   */
  addAgentResponse: (message: AgentResponse<TemplateCollection>) => void;
  /**
   * Clears the chat.
   */
  clearChat: () => void;
  /**
   * Stops the speaking.
   */
  stopSpeaking: () => void;
  /**
   * Activates the custom error state.
   */
  activateCustomErrorState: () => void;
  /**
   * Deactivates the custom error state.
   */
  deactivateCustomErrorState: () => void;
  /**
   * Activates the custom loading state.
   */
  activateCustomLoadingState: () => void;
  /**
   * Deactivates the custom loading state.
   */
  deactivateCustomLoadingState: () => void;
  /**
   * Whether the app is in a custom loading state.
   */
  isCustomLoading: boolean;
  /**
   * Stops all audio and chat messages, by calling the stop methods, and sending a stop websocket message to the backend.
   */
  stopAll: () => void;
};

export const AppControllerContext = createContext<
  AppControllerContextState | undefined
>(undefined);
